import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useTrail, animated, useSpring } from '@react-spring/web';
import api from "../api/axios";
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import PageTransition from '../components/PageTransition';
import LoadingSpinner from '../components/LoadingSpinner';
import { smoothTransitions } from '../utils/smoothTransitions';
import { useOptimizedAnimation } from '../hooks/useOptimizedAnimation';
import { useTheme } from '../contexts/ThemeContext';
import { useLanguage } from '../contexts/LanguageContext';

export default function Reports() {
  const { isDark } = useTheme();
  const { t, language } = useLanguage();
  const [reportData, setReportData] = useState({
    monthly: {},
    bookings: {},
    revenue: {},
    properties: {},
    users: {}
  });
  const [loading, setLoading] = useState(true);
  const [selectedPeriod, setSelectedPeriod] = useState('monthly');
  const [showResetModal, setShowResetModal] = useState(false);
  const [resetType, setResetType] = useState('');
  const [resetting, setResetting] = useState(false);
  const { getOptimizedVariants } = useOptimizedAnimation();

  // Animation hooks
  const headerSpring = useSpring({
    from: { opacity: 0, transform: 'translate3d(0,-50px,0)' },
    to: { opacity: 1, transform: 'translate3d(0,0px,0)' },
    config: { tension: 300, friction: 30 }
  });

  useEffect(() => {
    fetchReportData();
  }, [selectedPeriod]);

  const fetchReportData = async () => {
    try {
      const [monthlyReport, bookingReport, revenueReport, propertyReport, userReport] = await Promise.all([
        api.get('/reports/monthly'),
        api.get('/reports/bookings'),
        api.get('/reports/revenue'),
        api.get('/reports/properties'),
        api.get('/reports/users')
      ]);

      setReportData({
        monthly: monthlyReport.data.data,
        bookings: bookingReport.data.data,
        revenue: revenueReport.data.data,
        properties: propertyReport.data.data,
        users: userReport.data.data
      });
      setLoading(false);
    } catch (error) {
      console.error('Error fetching report data:', error);
      setLoading(false);
    }
  };

  const exportToPDF = () => {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.width;
    const now = new Date().toLocaleDateString(language === 'id' ? 'id-ID' : 'en-US');

    // === HEADER ===
    doc.setFontSize(20);
    doc.setTextColor(37, 99, 235);
    doc.text('Sewa Apartemen - Laporan Analitik', pageWidth / 2, 20, { align: 'center' });
    doc.setFontSize(10);
    doc.setTextColor(100, 116, 139);
    doc.text(`Dibuat pada: ${now}`, pageWidth / 2, 28, { align: 'center' });

    let yPos = 38;

    // === RINGKASAN UTAMA ===
    doc.setFontSize(13);
    doc.setTextColor(30, 41, 59);
    doc.text('Ringkasan Utama', 14, yPos);
    yPos += 2;

    autoTable(doc, {
      startY: yPos,
      head: [['Metrik', 'Nilai']],
      body: [
        ['Total Pendapatan', `Rp ${Math.round(reportData.revenue.total_revenue || 0).toLocaleString('id-ID')}`],
        ['Total Pengguna', String(reportData.users.total_users || 0)],
        ['Pengguna Aktif', String(reportData.users.active_users || 0)],
        ['Pengguna Baru Bulan Ini', String(reportData.users.new_users_this_month || 0)],
        ['Total Properti', String(reportData.properties.total_properties || 0)],
      ],
      headStyles: { fillColor: [37, 99, 235], textColor: 255, fontStyle: 'bold' },
      alternateRowStyles: { fillColor: [241, 245, 249] },
      margin: { left: 14, right: 14 }
    });
    yPos = doc.lastAutoTable.finalY + 10;

    // === PENDAPATAN BULANAN ===
    if ((reportData.revenue.monthly_revenue || []).length > 0) {
      doc.setFontSize(13);
      doc.setTextColor(30, 41, 59);
      doc.text('Pendapatan Bulanan', 14, yPos);
      yPos += 2;

      autoTable(doc, {
        startY: yPos,
        head: [['Bulan', 'Pendapatan', 'Transaksi']],
        body: (reportData.revenue.monthly_revenue || []).slice(0, 12).map(item => [
          item.month,
          `Rp ${Math.round(parseFloat(item.revenue) || 0).toLocaleString('id-ID')}`,
          String(item.transactions || 0)
        ]),
        headStyles: { fillColor: [16, 185, 129], textColor: 255, fontStyle: 'bold' },
        alternateRowStyles: { fillColor: [241, 245, 249] },
        margin: { left: 14, right: 14 }
      });
      yPos = doc.lastAutoTable.finalY + 10;
    }

    // === BOOKING PER STATUS ===
    if (yPos > 230) { doc.addPage(); yPos = 20; }
    if ((reportData.bookings.by_status || []).length > 0) {
      doc.setFontSize(13);
      doc.setTextColor(30, 41, 59);
      doc.text('Booking per Status', 14, yPos);
      yPos += 2;

      autoTable(doc, {
        startY: yPos,
        head: [['Status', 'Jumlah', 'Rata-rata Nilai']],
        body: (reportData.bookings.by_status || []).map(item => [
          item.status,
          String(item.count || 0),
          `Rp ${Math.round(parseFloat(item.avg_amount) || 0).toLocaleString('id-ID')}`
        ]),
        headStyles: { fillColor: [245, 158, 11], textColor: 255, fontStyle: 'bold' },
        alternateRowStyles: { fillColor: [241, 245, 249] },
        margin: { left: 14, right: 14 }
      });
      yPos = doc.lastAutoTable.finalY + 10;
    }

    // === PROPERTI PER STATUS ===
    if (yPos > 230) { doc.addPage(); yPos = 20; }
    if ((reportData.properties.by_status || []).length > 0) {
      doc.setFontSize(13);
      doc.setTextColor(30, 41, 59);
      doc.text('Properti per Status', 14, yPos);
      yPos += 2;

      autoTable(doc, {
        startY: yPos,
        head: [['Status', 'Jumlah']],
        body: (reportData.properties.by_status || []).map(item => [
          item.status, String(item.count || 0)
        ]),
        headStyles: { fillColor: [139, 92, 246], textColor: 255, fontStyle: 'bold' },
        alternateRowStyles: { fillColor: [241, 245, 249] },
        margin: { left: 14, right: 14 }
      });
      yPos = doc.lastAutoTable.finalY + 10;
    }

    // === TOP PROPERTI ===
    if (yPos > 200) { doc.addPage(); yPos = 20; }
    if ((reportData.properties.top_performing || []).length > 0) {
      doc.setFontSize(13);
      doc.setTextColor(30, 41, 59);
      doc.text('Properti Terbaik', 14, yPos);
      yPos += 2;

      autoTable(doc, {
        startY: yPos,
        head: [['Properti', 'Kota', 'Total Booking', 'Total Pendapatan']],
        body: (reportData.properties.top_performing || []).slice(0, 10).map(item => [
          item.name || '-',
          item.city || '-',
          String(item.booking_count || 0),
          `Rp ${Math.round(parseFloat(item.total_revenue) || 0).toLocaleString('id-ID')}`
        ]),
        headStyles: { fillColor: [37, 99, 235], textColor: 255, fontStyle: 'bold' },
        alternateRowStyles: { fillColor: [241, 245, 249] },
        margin: { left: 14, right: 14 }
      });
    }

    doc.save(`laporan_analitik_${new Date().toISOString().split('T')[0]}.pdf`);
  };

  const exportToExcel = () => {
    const data = {
      revenue: reportData.revenue,
      users: reportData.users,
      properties: reportData.properties,
      bookings: reportData.bookings
    };

    const jsonString = JSON.stringify(data, null, 2);
    const blob = new Blob([jsonString], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `reports_${new Date().toISOString().split('T')[0]}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const printReport = () => {
    window.print();
  };

  const handleResetClick = (type) => {
    setResetType(type);
    setShowResetModal(true);
  };

  const confirmReset = async () => {
    setResetting(true);
    try {
      const response = await api.post('/reports/reset', { reset_type: resetType });
      
      if (response.data.success) {
        alert(`✅ Reset berhasil!\n\nData yang dihapus:\n• Payments: ${response.data.data.deleted_payments}\n• Bookings: ${response.data.data.deleted_bookings}`);
        setShowResetModal(false);
        fetchReportData(); // Refresh data
      }
    } catch (error) {
      console.error('Error resetting reports:', error);
      alert('❌ Gagal mereset data: ' + (error.response?.data?.message || error.message));
    } finally {
      setResetting(false);
    }
  };

  if (loading) {
    return (
      <PageTransition>
        <div className="flex items-center justify-center min-h-96">
          <LoadingSpinner variant="reports" size="large" />
        </div>
      </PageTransition>
    );
  }

  return (
    <PageTransition>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="space-y-6"
      >
        {/* Header */}
        <animated.div
          style={headerSpring}
          className={`${isDark ? 'bg-gray-800/95 border-gray-700/20' : 'bg-white/95 border-white/20'} backdrop-blur-xl rounded-2xl shadow-xl border p-6`}
        >
          <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-4">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.2 }}
            >
              <motion.h1
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                className={`text-3xl font-bold ${isDark ? 'text-gray-100' : 'bg-gradient-to-r from-blue-600 to-blue-800 bg-clip-text text-transparent'}`}
              >
                {t('reports')}
              </motion.h1>
              <motion.p
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
                className={`${isDark ? 'text-gray-300' : 'text-gray-600'} mt-1`}
              >
                {t('comprehensive_business_reports')}
              </motion.p>
            </motion.div>
            <motion.select
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.5 }}
              whileHover={{ scale: 1.02 }}
              value={selectedPeriod}
              onChange={(e) => setSelectedPeriod(e.target.value)}
              className={`px-4 py-3 border rounded-xl focus:ring-2 transition-all hover:shadow-lg ${isDark ? 'border-gray-600 bg-gray-700 text-white focus:ring-gray-500 focus:border-gray-500' : 'border-gray-300 bg-white focus:ring-blue-500 focus:border-blue-500'}`}
            >
              <option value="monthly">{t('monthly')}</option>
              <option value="quarterly">{t('quarterly')}</option>
              <option value="yearly">{t('yearly')}</option>
            </motion.select>
          </div>
        </animated.div>

        {/* Revenue Overview Cards */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
          className="space-y-6"
        >
          <motion.h2
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.7 }}
            className={`text-2xl font-bold flex items-center ${isDark ? 'text-gray-100' : 'text-gray-900'}`}
          >
            <motion.i
              animate={{ rotate: [0, 10, -10, 0] }}
              transition={{ duration: 2, repeat: Infinity, repeatDelay: 3 }}
              className={`fas fa-chart-bar mr-3 ${isDark ? 'text-gray-400' : 'text-blue-600'}`}
            ></motion.i>
            {t('revenue_overview')}
          </motion.h2>
          <motion.div
            variants={getOptimizedVariants(smoothTransitions.containerVariants)}
            initial="initial"
            animate="animate"
            className="flex flex-wrap justify-center gap-6"
          >
            {(reportData.revenue.monthly_revenue || []).slice(0, 6).map((item, index) => (
              <motion.div
                key={index}
                variants={getOptimizedVariants(smoothTransitions.cardVariants)}
                custom={index}
                whileHover={{ scale: 1.05, y: -5, rotateY: 5 }}
                whileTap={{ scale: 0.95 }}
                className={`flex-1 min-w-0 ${isDark ? 'bg-gray-800/95 border-gray-700/20' : 'bg-white/95 border-white/20'} backdrop-blur-xl rounded-2xl shadow-xl border p-8 hover:shadow-2xl transition-all duration-300 cursor-pointer will-change-transform animate-gpu`}
              >
                <div className="flex items-center justify-between mb-4">
                  <motion.div
                    whileHover={{ rotate: 360, scale: 1.1 }}
                    transition={{ duration: 0.6 }}
                    className={`p-3 rounded-xl shadow-lg ${isDark ? 'bg-gradient-to-br from-gray-600 to-gray-700' : 'bg-gradient-to-br from-blue-500 to-blue-600'}`}
                  >
                    <i className="fas fa-calendar text-white text-lg"></i>
                  </motion.div>
                  <motion.span
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: index * 0.1 + 0.2 }}
                    className="text-sm font-medium text-gray-500 bg-gray-50 px-3 py-1 rounded-full"
                  >
                    {item.month}
                  </motion.span>
                </div>
                <div className="space-y-2">
                  <motion.p
                    initial={{ opacity: 0, scale: 0.5 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: index * 0.1 + 0.3, type: "spring" }}
                    className={`text-2xl font-bold ${isDark ? 'text-gray-100' : 'text-gray-900'}`}
                  >
                    Rp {parseFloat(item.revenue || 0).toLocaleString()}
                  </motion.p>
                  <motion.p
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: index * 0.1 + 0.4 }}
                    className={`text-sm flex items-center ${isDark ? 'text-gray-300' : 'text-gray-600'}`}
                  >
                    <motion.i
                      animate={{ scale: [1, 1.2, 1] }}
                      transition={{ duration: 2, repeat: Infinity, repeatDelay: 4 }}
                      className={`fas fa-exchange-alt mr-1 ${isDark ? 'text-gray-400' : 'text-blue-500'}`}
                    ></motion.i>
                    {item.transactions} {t('transactions')}
                  </motion.p>
                </div>
              </motion.div>
            ))}
          </motion.div>

          {/* Total Revenue Card */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.8 }}
            whileHover={{ scale: 1.02, y: -5 }}
            className={`rounded-2xl shadow-xl p-8 text-white cursor-pointer will-change-transform animate-gpu ${isDark ? 'bg-gradient-to-br from-gray-600 to-gray-700' : 'bg-gradient-to-br from-blue-500 to-blue-600'}`}
          >
            <div className="flex items-center justify-between">
              <div>
                <motion.h3
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.9 }}
                  className="text-lg font-semibold opacity-90"
                >
                  {t('total_revenue')}
                </motion.h3>
                <motion.p
                  initial={{ opacity: 0, scale: 0.5 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 1, type: "spring" }}
                  className="text-4xl font-bold mt-2"
                >
                  Rp {Math.round(reportData.revenue.total_revenue || 0).toLocaleString('id-ID')}
                </motion.p>
              </div>
              <motion.div
                whileHover={{ rotate: 360, scale: 1.1 }}
                transition={{ duration: 0.6 }}
                className="p-4 bg-white/20 rounded-xl"
              >
                <motion.i
                  animate={{ y: [0, -5, 0] }}
                  transition={{ duration: 2, repeat: Infinity }}
                  className="fas fa-money-bill-wave text-3xl"
                ></motion.i>
              </motion.div>
            </div>
          </motion.div>
        </motion.div>

        {/* Status Reports Cards */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Bookings Status Cards */}
          <div className="space-y-4">
            <h2 className={`text-xl font-bold flex items-center ${isDark ? 'text-gray-100' : 'text-gray-900'}`}>
              <i className={`fas fa-calendar-check mr-3 ${isDark ? 'text-gray-400' : 'text-blue-600'}`}></i>
              {t('bookings_by_status')}
            </h2>
            <div className="grid gap-4">
              {(reportData.bookings.by_status || []).map((item, index) => {
                const colors = {
                  active: isDark ? 'from-gray-600 to-gray-700' : 'from-green-500 to-green-600',
                  pending: isDark ? 'from-gray-600 to-gray-700' : 'from-yellow-500 to-yellow-600',
                  completed: isDark ? 'from-gray-600 to-gray-700' : 'from-blue-500 to-blue-600',
                  cancelled: isDark ? 'from-gray-600 to-gray-700' : 'from-red-500 to-red-600'
                };
                const icons = {
                  active: 'fa-check-circle',
                  pending: 'fa-clock',
                  completed: 'fa-flag-checkered',
                  cancelled: 'fa-times-circle'
                };
                return (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.1 }}
                    className="bg-white rounded-xl shadow-lg border border-gray-100 p-6 hover:shadow-xl transition-all duration-300"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center">
                        <div className={`p-3 bg-gradient-to-br ${colors[item.status] || 'from-gray-500 to-gray-600'} rounded-xl mr-4`}>
                          <i className={`fas ${icons[item.status] || 'fa-circle'} text-white text-lg`}></i>
                        </div>
                        <span className="capitalize font-semibold text-gray-900">{item.status}</span>
                      </div>
                      <span className="text-2xl font-bold text-gray-900">{item.count}</span>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          </div>

          {/* Properties Status Cards */}
          <div className="space-y-4">
            <h2 className={`text-xl font-bold flex items-center ${isDark ? 'text-gray-100' : 'text-gray-900'}`}>
              <i className={`fas fa-building mr-3 ${isDark ? 'text-gray-400' : 'text-blue-600'}`}></i>
              {t('properties_by_status')}
            </h2>
            <div className="grid gap-4">
              {(reportData.properties.by_status || []).map((item, index) => {
                const colors = {
                  available: isDark ? 'from-gray-600 to-gray-700' : 'from-green-500 to-green-600',
                  rented: isDark ? 'from-gray-600 to-gray-700' : 'from-blue-500 to-blue-600',
                  maintenance: isDark ? 'from-gray-600 to-gray-700' : 'from-orange-500 to-orange-600'
                };
                const icons = {
                  available: 'fa-home',
                  rented: 'fa-key',
                  maintenance: 'fa-tools'
                };
                return (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.1 }}
                    className="bg-white rounded-xl shadow-lg border border-gray-100 p-6 hover:shadow-xl transition-all duration-300"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center">
                        <div className={`p-3 bg-gradient-to-br ${colors[item.status] || 'from-gray-500 to-gray-600'} rounded-xl mr-4`}>
                          <i className={`fas ${icons[item.status] || 'fa-building'} text-white text-lg`}></i>
                        </div>
                        <span className="capitalize font-semibold text-gray-900">{item.status}</span>
                      </div>
                      <span className="text-2xl font-bold text-gray-900">{item.count}</span>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </div>

        {/* User Statistics Cards */}
        <div className="space-y-6">
          <h2 className={`text-2xl font-bold flex items-center ${isDark ? 'text-gray-100' : 'text-gray-900'}`}>
            <i className={`fas fa-users mr-3 ${isDark ? 'text-gray-400' : 'text-blue-600'}`}></i>
            {t('user_statistics')}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { title: t('total_users'), value: reportData.users.total_users || 0, icon: 'fa-users', gradient: isDark ? 'from-gray-600 to-gray-700' : 'from-blue-500 to-blue-600' },
              { title: t('active_users'), value: reportData.users.active_users || 0, icon: 'fa-user-check', gradient: isDark ? 'from-gray-600 to-gray-700' : 'from-green-500 to-green-600' },
              { title: t('new_this_month'), value: reportData.users.new_users_this_month || 0, icon: 'fa-user-plus', gradient: isDark ? 'from-gray-600 to-gray-700' : 'from-purple-500 to-purple-600' },
              { title: t('total_properties'), value: reportData.properties.total_properties || 0, icon: 'fa-building', gradient: isDark ? 'from-gray-600 to-gray-700' : 'from-orange-500 to-orange-600' }
            ].map((stat, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6 hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1"
              >
                <div className="flex items-center justify-between mb-4">
                  <div className={`p-3 bg-gradient-to-br ${stat.gradient} rounded-xl`}>
                    <i className={`fas ${stat.icon} text-white text-lg`}></i>
                  </div>
                </div>
                <div className="space-y-2">
                  <h3 className="font-semibold text-gray-600">{stat.title}</h3>
                  <p className="text-3xl font-bold text-gray-900">{stat.value}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Export Options Cards */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.2 }}
          className="space-y-6"
        >
          <motion.h2
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 1.3 }}
            className={`text-2xl font-bold flex items-center ${isDark ? 'text-gray-100' : 'text-gray-900'}`}
          >
            <motion.i
              animate={{ y: [0, -3, 0] }}
              transition={{ duration: 2, repeat: Infinity }}
              className={`fas fa-download mr-3 ${isDark ? 'text-gray-400' : 'text-blue-600'}`}
            ></motion.i>
            {t('export_reports')}
          </motion.h2>
          <motion.div
            variants={getOptimizedVariants(smoothTransitions.containerVariants)}
            initial="initial"
            animate="animate"
            className="grid grid-cols-1 md:grid-cols-3 gap-6"
          >
            <motion.button
              variants={getOptimizedVariants(smoothTransitions.cardVariants)}
              whileHover={{ scale: 1.05, y: -5 }}
              whileTap={{ scale: 0.95 }}
              onClick={exportToPDF}
              className="bg-white/95 backdrop-blur-xl rounded-2xl shadow-xl border border-white/20 p-6 hover:shadow-2xl transition-all duration-300 group will-change-transform animate-gpu"
            >
              <div className="flex flex-col items-center text-center space-y-4">
                <motion.div
                  whileHover={{ rotate: 360, scale: 1.2 }}
                  transition={{ duration: 0.6 }}
                  className={`p-4 rounded-xl shadow-lg ${isDark ? 'bg-gradient-to-br from-gray-600 to-gray-700' : 'bg-gradient-to-br from-red-500 to-red-600'}`}
                >
                  <motion.i
                    animate={{ rotate: [0, 5, -5, 0] }}
                    transition={{ duration: 2, repeat: Infinity, repeatDelay: 3 }}
                    className="fas fa-file-pdf text-white text-2xl"
                  ></motion.i>
                </motion.div>
                <div>
                  <h3 className="font-bold text-gray-900 mb-1">{t('export_to_pdf')}</h3>
                  <p className="text-sm text-gray-600">{t('download_comprehensive_report')}</p>
                </div>
              </div>
            </motion.button>

            <motion.button
              variants={getOptimizedVariants(smoothTransitions.cardVariants)}
              whileHover={{ scale: 1.05, y: -5 }}
              whileTap={{ scale: 0.95 }}
              onClick={exportToExcel}
              className="bg-white/95 backdrop-blur-xl rounded-2xl shadow-xl border border-white/20 p-6 hover:shadow-2xl transition-all duration-300 group will-change-transform animate-gpu"
            >
              <div className="flex flex-col items-center text-center space-y-4">
                <motion.div
                  whileHover={{ rotate: 360, scale: 1.2 }}
                  transition={{ duration: 0.6 }}
                  className={`p-4 rounded-xl shadow-lg ${isDark ? 'bg-gradient-to-br from-gray-600 to-gray-700' : 'bg-gradient-to-br from-green-500 to-green-600'}`}
                >
                  <motion.i
                    animate={{ scale: [1, 1.1, 1] }}
                    transition={{ duration: 2, repeat: Infinity, repeatDelay: 4 }}
                    className="fas fa-file-excel text-white text-2xl"
                  ></motion.i>
                </motion.div>
                <div>
                  <h3 className="font-bold text-gray-900 mb-1">{t('export_data')}</h3>
                  <p className="text-sm text-gray-600">{t('download_raw_data_file')}</p>
                </div>
              </div>
            </motion.button>

            <motion.button
              variants={getOptimizedVariants(smoothTransitions.cardVariants)}
              whileHover={{ scale: 1.05, y: -5 }}
              whileTap={{ scale: 0.95 }}
              onClick={printReport}
              className={`${isDark ? 'bg-gray-800/95 border-gray-700/20' : 'bg-white/95 border-white/20'} backdrop-blur-xl rounded-2xl shadow-xl border p-6 hover:shadow-2xl transition-all duration-300 group will-change-transform animate-gpu`}
            >
              <div className="flex flex-col items-center text-center space-y-4">
                <motion.div
                  whileHover={{ rotate: 360, scale: 1.2 }}
                  transition={{ duration: 0.6 }}
                  className={`p-4 rounded-xl shadow-lg ${isDark ? 'bg-gradient-to-br from-gray-600 to-gray-700' : 'bg-gradient-to-br from-blue-500 to-blue-600'}`}
                >
                  <motion.i
                    animate={{ y: [0, -2, 0] }}
                    transition={{ duration: 1.5, repeat: Infinity, repeatDelay: 2 }}
                    className="fas fa-print text-white text-2xl"
                  ></motion.i>
                </motion.div>
                <div>
                  <h3 className={`font-bold mb-1 ${isDark ? 'text-gray-100' : 'text-gray-900'}`}>{t('print_report')}</h3>
                  <p className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>{t('print_current_view')}</p>
                </div>
              </div>
            </motion.button>
          </motion.div>
        </motion.div>

        {/* Reset Data Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.4 }}
          className="space-y-6"
        >
          <motion.h2
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 1.5 }}
            className={`text-2xl font-bold flex items-center ${isDark ? 'text-red-400' : 'text-red-600'}`}
          >
            <motion.i
              animate={{ rotate: [0, 10, -10, 0] }}
              transition={{ duration: 2, repeat: Infinity }}
              className="fas fa-exclamation-triangle mr-3"
            ></motion.i>
            Reset Data Laporan
          </motion.h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => handleResetClick('failed_payments')}
              className="bg-yellow-500 hover:bg-yellow-600 text-white rounded-xl p-4 shadow-lg transition-all"
            >
              <i className="fas fa-times-circle mb-2 text-2xl"></i>
              <div className="font-bold">Reset Failed Payments</div>
              <div className="text-xs opacity-90">Hapus payments pending/failed</div>
            </motion.button>

            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => handleResetClick('cancelled_bookings')}
              className="bg-orange-500 hover:bg-orange-600 text-white rounded-xl p-4 shadow-lg transition-all"
            >
              <i className="fas fa-ban mb-2 text-2xl"></i>
              <div className="font-bold">Reset Cancelled</div>
              <div className="text-xs opacity-90">Hapus bookings cancelled</div>
            </motion.button>

            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => handleResetClick('current_month')}
              className="bg-red-500 hover:bg-red-600 text-white rounded-xl p-4 shadow-lg transition-all"
            >
              <i className="fas fa-calendar-times mb-2 text-2xl"></i>
              <div className="font-bold">Reset Bulan Ini</div>
              <div className="text-xs opacity-90">Hapus data bulan ini</div>
            </motion.button>

            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => handleResetClick('all')}
              className="bg-gray-800 hover:bg-gray-900 text-white rounded-xl p-4 shadow-lg transition-all"
            >
              <i className="fas fa-trash-alt mb-2 text-2xl"></i>
              <div className="font-bold">Reset SEMUA</div>
              <div className="text-xs opacity-90">⚠️ Hapus semua data!</div>
            </motion.button>
          </div>
        </motion.div>

        {/* Reset Confirmation Modal */}
        {showResetModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
            onClick={() => !resetting && setShowResetModal(false)}
          >
            <motion.div
              initial={{ scale: 0.9, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="text-center">
                <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-red-100 mb-4">
                  <i className="fas fa-exclamation-triangle text-red-600 text-2xl"></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Konfirmasi Reset</h3>
                <p className="text-gray-600 mb-6">
                  {resetType === 'all' && '⚠️ Ini akan menghapus SEMUA data payments dan bookings! Tindakan ini tidak dapat dibatalkan.'}
                  {resetType === 'failed_payments' && 'Menghapus semua payments dengan status pending atau failed.'}
                  {resetType === 'cancelled_bookings' && 'Menghapus semua bookings dengan status cancelled.'}
                  {resetType === 'current_month' && 'Menghapus semua data payments dan bookings bulan ini.'}
                </p>
                <div className="flex gap-3">
                  <button
                    onClick={() => setShowResetModal(false)}
                    disabled={resetting}
                    className="flex-1 px-4 py-3 bg-gray-200 text-gray-800 rounded-xl font-semibold hover:bg-gray-300 transition-all disabled:opacity-50"
                  >
                    Batal
                  </button>
                  <button
                    onClick={confirmReset}
                    disabled={resetting}
                    className="flex-1 px-4 py-3 bg-red-600 text-white rounded-xl font-semibold hover:bg-red-700 transition-all disabled:opacity-50 flex items-center justify-center"
                  >
                    {resetting ? (
                      <>
                        <i className="fas fa-spinner fa-spin mr-2"></i>
                        Mereset...
                      </>
                    ) : (
                      <>
                        <i className="fas fa-check mr-2"></i>
                        Ya, Reset
                      </>
                    )}
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </motion.div>
    </PageTransition>
  );
}